import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Clock,
  Cloud,
  Database,
  FileText,
  Globe,
  ImageIcon,
  Loader2,
  Paperclip,
  Play,
  X,
  Zap,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ReviewModal } from "@/components/flows/ReviewModal";
import { Markdown } from "@/components/shared/Markdown";
import { VoiceInputButton } from "@/components/shared/VoiceInputButton";
import { ALLOWED_IMAGE_TYPES } from "@/constants/files";
import { useOperations } from "@/context/OperationContext";
import { useChatAttachments } from "@/hooks/useChatAttachments";
import { type OperationEvent, useOperationStream } from "@/hooks/useOperationStream";
import { dataSourceService, flowService } from "@/lib/services";
import type { DataCollection, Flow, FlowInputType, FlowRun } from "@/types";

type StepStatus = "idle" | "running" | "completed" | "failed" | "error" | "paused";
type RunStatus = "idle" | "running" | "completed" | "error" | "paused";

interface StepResult {
  id: number;
  stepNumber: number;
  agentUuid?: string;
  agentName?: string;
  status: StepStatus;
  tokensUsed: number;
  outputContent?: string;
  errorMessage?: string;
}

function ViewRunDetailsButton({
  flowId,
  runId,
  variant = "secondary",
}: Readonly<{
  flowId: string;
  runId: string;
  variant?: "secondary" | "primary";
}>) {
  const navigate = useNavigate();
  const styles =
    variant === "primary"
      ? "text-brand-primary border-brand-primary/30 hover:bg-brand-primary/10"
      : "text-content-secondary border-border-secondary hover:text-content-primary hover:border-content-tertiary";
  return (
    <button
      type="button"
      onClick={() => navigate(`/flows/${flowId}/runs/${runId}`)}
      className={`px-3 py-1.5 text-sm border rounded-lg transition-colors ${styles}`}
    >
      View Run Details
    </button>
  );
}

export function FlowRunner() {
  const { flowId } = useParams<{ flowId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { trackOperation } = useOperations();
  const [flow, setFlow] = useState<Flow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inputType, setInputType] = useState<FlowInputType>("prompt");
  const [input, setInput] = useState("");
  const [webUrl, setWebUrl] = useState("");
  const [selectedDataSource, setSelectedDataSource] = useState<string>("");
  const [dataSources, setDataSources] = useState<DataCollection[]>([]);
  const [status, setStatus] = useState<RunStatus>("idle");
  const [stepResults, setStepResults] = useState<StepResult[]>([]);
  const [output, setOutput] = useState("");
  const [currentRunId, setCurrentRunId] = useState<string | null>(null);
  const [pausedFlowRun, setPausedFlowRun] = useState<FlowRun | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const autoStartTriggered = useRef(false);

  const isAutoStart = searchParams.get("autostart") === "1";
  const prefillInput = searchParams.get("input");

  // File attachment handling via shared hook
  const attachments = useChatAttachments({
    modelSupportsVision: false, // Flows don't use vision models directly
    inputValue: input,
    setInputValue: setInput,
  });

  // Operation Manager stream
  const [operationId, setOperationId] = useState<string | null>(null);

  // ── Event handler for operation stream ─────────────────────────
  const handleFlowEvent = useCallback(
    (event: OperationEvent) => {
      const { event: eventName, data } = event;

      switch (eventName) {
        case "flow_start": {
          const d = data as { run_uuid: string };
          setCurrentRunId(d.run_uuid);
          break;
        }

        case "step_start": {
          const d = data as { step_number: number; agent_name: string; agent_uuid: string };
          setStepResults((prev) => [
            ...prev,
            {
              id: prev.length + 1,
              stepNumber: d.step_number,
              agentUuid: d.agent_uuid,
              agentName: d.agent_name,
              status: "running",
              tokensUsed: 0,
            },
          ]);
          break;
        }

        case "step_content": {
          const d = data as { step_number: number; content: string };
          setStepResults((prev) =>
            prev.map((r) =>
              r.stepNumber === d.step_number ? { ...r, outputContent: (r.outputContent || "") + d.content } : r,
            ),
          );
          break;
        }

        case "step_end": {
          const d = data as {
            step_number: number;
            output: string;
            tokens_used: number;
            status: string;
          };
          setStepResults((prev) =>
            prev.map((r) =>
              r.stepNumber === d.step_number
                ? {
                    ...r,
                    status: "completed" as StepStatus,
                    outputContent: d.output,
                    tokensUsed: d.tokens_used,
                  }
                : r,
            ),
          );
          break;
        }

        case "step_paused": {
          const d = data as { step_number: number; message: string; run_uuid: string };
          setStepResults((prev) =>
            prev.map((r) => (r.stepNumber === d.step_number ? { ...r, status: "paused" as StepStatus } : r)),
          );
          setStatus("paused");
          setCurrentRunId(d.run_uuid);
          // Load the full run details to show in review modal
          if (flowId) {
            flowService.getRun(flowId, d.run_uuid).then((runResponse) => {
              setPausedFlowRun(runResponse.data);
              setShowReviewModal(true);
            });
          }
          break;
        }

        case "step_failed": {
          const d = data as { step_number: number; error: string };
          setStepResults((prev) =>
            prev.map((r) =>
              r.stepNumber === d.step_number ? { ...r, status: "failed" as StepStatus, errorMessage: d.error } : r,
            ),
          );
          break;
        }

        case "flow_end": {
          const d = data as { output: string; total_tokens: number };
          setOutput(d.output);
          setStatus("completed");
          break;
        }

        case "flow_paused": {
          setStatus("paused");
          break;
        }

        case "flow_failed": {
          const d = data as { error: string };
          setError(d.error);
          setStatus("error");
          break;
        }

        case "operation_completed": {
          // Terminal — if we haven't already set a final status, do it now.
          if (status === "running") {
            setStatus("completed");
          }
          break;
        }

        case "operation_failed": {
          const d = data as { error?: string };
          if (status === "running") {
            setError(d.error || "Operation failed");
            setStatus("error");
          }
          break;
        }

        case "error": {
          const d = data as { message: string };
          setError(d.message);
          setStatus("error");
          break;
        }
      }
    },
    [flowId, status],
  );

  const { abort: _abortStream } = useOperationStream(operationId, {
    onEvent: handleFlowEvent,
    onError: (err) => {
      setError(err);
      setStatus("error");
    },
  });

  useEffect(() => {
    if (flowId) {
      loadFlow();
      loadDataSources();
    }
  }, [flowId]);

  const loadDataSources = async () => {
    try {
      const response = await dataSourceService.list();
      setDataSources(response.data);
    } catch (err) {
      console.error("Error loading data sources:", err);
    }
  };

  const loadFlow = async () => {
    if (!flowId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await flowService.get(flowId);
      setFlow(response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load flow");
      console.error("Error loading flow:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRunFlow = useCallback(
    async (overridePrompt?: string) => {
      const effectiveInput = overridePrompt || input;
      // Validate input based on type — self-contained flows (steps have prompts) can run without input
      const stepsHavePrompts = flow?.steps?.some((s) => s.config?.prompt || s.config?.instructions);
      if (inputType === "prompt" && !effectiveInput.trim()) {
        if (!flow?.defaultInputPrompt && !stepsHavePrompts && attachments.uploadedFiles.length === 0) return;
      }
      if (inputType === "web_url" && !webUrl.trim()) return;
      if (inputType === "data_collection" && !selectedDataSource) return;

      setStatus("running");
      setStepResults([]);
      setOutput("");
      setCurrentRunId(null);
      setPausedFlowRun(null);
      setShowReviewModal(false);
      setError(null);
      setOperationId(null);

      try {
        // Upload any pending files first
        let fileBlobPaths: string[] = [];
        const { imageFiles, documentFiles } = attachments.consumePendingFiles();
        const allFiles = [...imageFiles, ...documentFiles];
        if (allFiles.length > 0) {
          const uploadResult = await flowService.uploadFiles(flowId!, allFiles);
          fileBlobPaths = uploadResult.files.map((f) => f.blob_path);
        }

        // Build input values based on type
        const inputValues: Record<string, string> = {};
        let inputPrompt = "";

        switch (inputType) {
          case "prompt":
            inputPrompt = effectiveInput.trim() || flow?.defaultInputPrompt || "";
            break;
          case "web_url":
            inputPrompt = `Process content from: ${webUrl}`;
            inputValues.web_url = webUrl;
            break;
          case "data_collection":
            inputPrompt = `Process data from collection: ${selectedDataSource}`;
            inputValues.data_collection_id = selectedDataSource;
            break;
        }

        const result = await flowService.runStream(flowId!, inputPrompt, inputValues, fileBlobPaths);
        setOperationId(result.operation_id);
        trackOperation({
          operation_id: result.operation_id,
          operation_type: "flow_run",
          entity_id: flowId!,
          status: "running",
          title: flow?.name || "Flow run",
          started_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          user_id: 0,
        });
      } catch (err) {
        console.error("Error running flow:", err);
        setError(err instanceof Error ? err.message : "Failed to run flow");
        setStatus("error");
      }
    },
    [flowId, input, inputType, webUrl, selectedDataSource, flow, attachments],
  );

  // Auto-start: when ?autostart=1 is present and the flow has loaded,
  // use the flow's default prompt (or prefill from URL) and trigger the run
  useEffect(() => {
    if (!isAutoStart || !flow || autoStartTriggered.current || status !== "idle") return;
    autoStartTriggered.current = true;

    const prompt = prefillInput || flow.defaultInputPrompt;
    if (prompt) {
      setInput(prompt);
      handleRunFlow(prompt);
    }
  }, [isAutoStart, flow, status, prefillInput, handleRunFlow]);

  const handleApproveReview = async (modifiedOutput?: string) => {
    if (!flowId || !currentRunId) return;

    try {
      setIsResuming(true);
      setShowReviewModal(false);
      setStatus("running");

      // Mark the paused step as completed locally for immediate feedback
      setStepResults((prev) =>
        prev.map((r) => (r.status === "paused" ? { ...r, status: "completed" as StepStatus } : r)),
      );

      // Resume the flow — backend returns immediately, executes remaining
      // steps as a background task.
      await flowService.resumeRun(flowId, currentRunId, true, modifiedOutput);

      // Poll for progress until the run reaches a terminal state
      const pollInterval = setInterval(async () => {
        try {
          const runResponse = await flowService.getRun(flowId, currentRunId);
          const run = runResponse.data;

          // Update step results from the backend state
          if (run.steps.length > 0) {
            setStepResults(
              run.steps.map((step) => ({
                id: step.id,
                stepNumber: step.stepNumber,
                agentUuid: step.agentUuid,
                agentName: step.agentName,
                status: step.status as StepStatus,
                tokensUsed: step.tokensUsed,
                outputContent: step.outputContent,
                errorMessage: step.errorMessage,
              })),
            );
          }

          // Check if run reached a terminal state or paused again
          if (run.status === "completed") {
            clearInterval(pollInterval);
            setOutput(run.steps[run.steps.length - 1]?.outputContent || "");
            setStatus("completed");
            setIsResuming(false);
          } else if (run.status === "paused") {
            clearInterval(pollInterval);
            setPausedFlowRun(run);
            setShowReviewModal(true);
            setStatus("paused");
            setIsResuming(false);
          } else if (run.status === "failed") {
            clearInterval(pollInterval);
            setError(run.errorMessage || "Flow failed");
            setStatus("error");
            setIsResuming(false);
          }
        } catch (err) {
          console.error("Error polling run status:", err);
          clearInterval(pollInterval);
          setError("Failed to check run status");
          setStatus("error");
          setIsResuming(false);
        }
      }, 2000); // Poll every 2 seconds
    } catch (err) {
      console.error("Error resuming flow:", err);
      setError(err instanceof Error ? err.message : "Failed to resume flow");
      setStatus("error");
      setIsResuming(false);
    }
  };

  const handleRejectReview = async () => {
    if (!flowId || !currentRunId) return;

    try {
      setIsResuming(true);
      await flowService.cancelRun(flowId, currentRunId, "Rejected by user during review");
      setShowReviewModal(false);
      setPausedFlowRun(null);
      setStatus("error");
      setError("Flow cancelled by user");
    } catch (err) {
      console.error("Error cancelling flow:", err);
      setError(err instanceof Error ? err.message : "Failed to cancel flow");
    } finally {
      setIsResuming(false);
    }
  };

  const getStatusIcon = (stepStatus: StepStatus) => {
    switch (stepStatus) {
      case "running":
        return <Loader2 className="h-4 w-4 text-blue-400 animate-spin" />;
      case "completed":
        return <CheckCircle2 className="h-4 w-4 text-green-400" />;
      case "error":
      case "failed":
        return <AlertCircle className="h-4 w-4 text-red-400" />;
      case "paused":
        return <Clock className="h-4 w-4 text-yellow-400" />;
      default:
        return <Clock className="h-4 w-4 text-content-quaternary" />;
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-brand-primary animate-spin" />
      </div>
    );
  }

  if (error && !flow) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
        <h3 className="text-lg font-medium text-content-primary">Error loading flow</h3>
        <p className="text-content-tertiary mt-1">{error}</p>
        <button
          onClick={() => navigate("/flows")}
          className="mt-4 px-4 py-2 bg-surface-secondary text-content-primary rounded-lg hover:bg-interactive-active transition-colors"
        >
          Back to Flows
        </button>
      </div>
    );
  }

  if (!flow) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <AlertCircle className="h-12 w-12 text-content-tertiary mb-4" />
        <h3 className="text-lg font-medium text-content-primary">Flow not found</h3>
        <button
          onClick={() => navigate("/flows")}
          className="mt-4 px-4 py-2 bg-surface-secondary text-content-primary rounded-lg hover:bg-interactive-active transition-colors"
        >
          Back to Flows
        </button>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="shrink-0 p-6 border-b border-border-primary">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/flows")}
            className="p-2 text-content-tertiary hover:text-content-primary hover:bg-interactive-hover rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-content-primary">{flow.name}</h1>
            <p className="text-content-tertiary mt-1">{flow.description || "No description"}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Input Section */}
          <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-lg bg-linear-to-br from-mutual-orange/20 to-mutual-purple/20 flex items-center justify-center">
                <FileText className="h-5 w-5 text-brand-primary" />
              </div>
              <div>
                <h2 className="font-medium text-content-primary">
                  {flow?.steps?.some((s) => s.config?.prompt || s.config?.instructions) ? "Ready to Run" : "Flow Input"}
                </h2>
                <p className="text-sm text-content-tertiary">
                  {flow?.steps?.some((s) => s.config?.prompt || s.config?.instructions)
                    ? "This flow has pre-configured steps. Click Run or add optional context below."
                    : "Choose your input type and provide the data"}
                </p>
              </div>
            </div>

            {/* Show step summary for self-contained flows */}
            {flow?.steps?.some((s) => s.config?.prompt || s.config?.instructions) && (
              <div className="mb-4 space-y-2">
                {flow.steps.map((step, idx) => (
                  <div
                    key={step.id}
                    className="flex items-start gap-3 p-3 bg-surface-secondary/50 rounded-lg border border-border-secondary"
                  >
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-brand-primary/10 text-brand-primary text-xs font-bold shrink-0 mt-0.5">
                      {idx + 1}
                    </span>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-content-primary">
                        {(step.config?.label as string) || step.agentName || "Step"}
                      </div>
                      {(step.config?.prompt != null || step.config?.instructions != null) && (
                        <div className="text-xs text-content-tertiary mt-0.5 line-clamp-2">
                          {String(step.config.prompt || step.config.instructions)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Input Type Selector — only show for flows that need input */}
            {!flow?.steps?.some((s) => s.config?.prompt || s.config?.instructions) && (
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setInputType("prompt")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    inputType === "prompt"
                      ? "bg-brand-primary text-white"
                      : "bg-surface-secondary text-content-secondary hover:bg-interactive-active"
                  }`}
                >
                  <FileText className="h-4 w-4" />
                  Prompt
                </button>
                <button
                  onClick={() => setInputType("web_url")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    inputType === "web_url"
                      ? "bg-brand-primary text-white"
                      : "bg-surface-secondary text-content-secondary hover:bg-interactive-active"
                  }`}
                >
                  <Globe className="h-4 w-4" />
                  Web URL
                </button>
                <button
                  onClick={() => setInputType("data_collection")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    inputType === "data_collection"
                      ? "bg-brand-primary text-white"
                      : "bg-surface-secondary text-content-secondary hover:bg-interactive-active"
                  }`}
                >
                  <Database className="h-4 w-4" />
                  Data Source
                </button>
              </div>
            )}

            {/* Input Fields */}
            {inputType === "prompt" && !flow?.steps?.some((s) => s.config?.prompt || s.config?.instructions) && (
              <div
                className={`space-y-3 ${attachments.isDraggingOver ? "ring-2 ring-brand-primary ring-dashed rounded-lg" : ""}`}
                onDragOver={attachments.handleDragOver}
                onDragLeave={attachments.handleDragLeave}
                onDrop={attachments.handleDrop}
              >
                {/* Drag overlay hint */}
                {attachments.isDraggingOver && (
                  <div className="flex items-center justify-center gap-2 py-3 bg-brand-primary-subtle border border-brand-primary/30 rounded-lg">
                    <ImageIcon className="w-5 h-5 text-brand-primary" />
                    <span className="text-sm text-brand-primary font-medium">Drop files here</span>
                  </div>
                )}

                {/* File previews */}
                {attachments.uploadedFiles.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {attachments.uploadedFiles.map((file, index) => {
                      const isImage = ALLOWED_IMAGE_TYPES.has(file.type);
                      return (
                        <div key={`upload-${file.name}-${index}`} className="relative group">
                          {isImage ? (
                            <img
                              src={URL.createObjectURL(file)}
                              alt={file.name}
                              className="w-16 h-16 rounded-lg object-cover border border-border-secondary"
                            />
                          ) : (
                            <div className="flex items-center gap-2 bg-surface-secondary pl-3 pr-2 py-2 rounded-lg border border-border-secondary">
                              <Paperclip className="w-3.5 h-3.5 text-content-tertiary shrink-0" />
                              <span className="text-sm text-content-secondary truncate max-w-[200px]">{file.name}</span>
                            </div>
                          )}
                          <button
                            onClick={() => attachments.removeFile(index)}
                            className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-surface-tertiary hover:bg-red-500 rounded-full flex items-center justify-center transition opacity-0 group-hover:opacity-100"
                          >
                            <X className="w-3 h-3 text-white" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* SharePoint file chips */}
                {(attachments.sharepointFiles.length > 0 || attachments.isResolvingUrl) && (
                  <div className="flex flex-wrap gap-2">
                    {attachments.sharepointFiles.map((file, index) => (
                      <div
                        key={file.id}
                        className="flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 pl-3 pr-2 py-2 rounded-lg"
                      >
                        <Cloud className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                        <span className="text-sm text-content-secondary truncate max-w-[200px]">{file.name}</span>
                        <button
                          onClick={() => attachments.setSharepointFiles((prev) => prev.filter((_, i) => i !== index))}
                          className="p-1 hover:bg-interactive-active rounded-sm transition"
                        >
                          <X className="w-3 h-3 text-content-tertiary" />
                        </button>
                      </div>
                    ))}
                    {attachments.isResolvingUrl && (
                      <div className="flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 pl-3 pr-3 py-2 rounded-lg">
                        <Loader2 className="w-3.5 h-3.5 text-blue-400 animate-spin shrink-0" />
                        <span className="text-sm text-content-tertiary">Resolving link...</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Prompt textarea + action buttons */}
                <div className="flex gap-3">
                  <label className="flex items-center justify-center w-10 h-10 bg-surface-secondary hover:bg-interactive-active rounded-lg cursor-pointer transition border border-border-secondary shrink-0 self-end">
                    <Paperclip className="w-5 h-5 text-content-tertiary" />
                    <input type="file" multiple onChange={attachments.handleFileUpload} className="hidden" />
                  </label>
                  <textarea
                    placeholder={
                      flow?.defaultInputPrompt
                        ? `Default: ${flow.defaultInputPrompt.slice(0, 100)}${flow.defaultInputPrompt.length > 100 ? "..." : ""}`
                        : "Enter your input for the flow..."
                    }
                    rows={4}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onPaste={attachments.handlePaste}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleRunFlow();
                      }
                    }}
                    disabled={status === "running"}
                    className="flex-1 px-4 py-3 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary resize-none disabled:opacity-50"
                  />
                  <VoiceInputButton
                    onTranscript={(text) => setInput((prev) => (prev ? `${prev} ${text}` : text))}
                    disabled={status === "running"}
                    className="self-start"
                  />
                </div>
                <p className="text-xs text-content-quaternary">
                  Press Enter to run, Shift+Enter for new line. Drag & drop or paste files.
                </p>
              </div>
            )}

            {inputType === "web_url" && !flow?.steps?.some((s) => s.config?.prompt || s.config?.instructions) && (
              <div>
                <input
                  type="url"
                  placeholder="https://example.com/page"
                  value={webUrl}
                  onChange={(e) => setWebUrl(e.target.value)}
                  disabled={status === "running"}
                  className="w-full px-4 py-3 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary disabled:opacity-50"
                />
                <p className="text-xs text-content-quaternary mt-2">
                  The flow will fetch and process content from this URL
                </p>
              </div>
            )}

            {inputType === "data_collection" &&
              !flow?.steps?.some((s) => s.config?.prompt || s.config?.instructions) && (
                <div>
                  <select
                    value={selectedDataSource}
                    onChange={(e) => setSelectedDataSource(e.target.value)}
                    disabled={status === "running"}
                    className="w-full px-4 py-3 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary disabled:opacity-50"
                  >
                    <option value="">Select a data source...</option>
                    {dataSources.map((ds) => (
                      <option key={ds.uuid} value={ds.uuid}>
                        {ds.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-content-quaternary mt-2">
                    The flow will process documents from this data collection
                  </p>
                </div>
              )}

            <button
              onClick={() => handleRunFlow()}
              disabled={status === "running"}
              className="mt-4 flex items-center gap-2 px-6 py-2.5 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {status === "running" ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  Run Flow
                </>
              )}
            </button>
          </div>

          {/* Steps Progress */}
          {(status === "running" || status === "completed" || status === "error" || status === "paused") &&
            stepResults.length > 0 && (
              <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-lg bg-linear-to-br from-mutual-orange/20 to-mutual-purple/20 flex items-center justify-center">
                    <Zap className="h-5 w-5 text-brand-primary" />
                  </div>
                  <div className="flex-1">
                    <h2 className="font-medium text-content-primary">Execution Progress</h2>
                    <p className="text-sm text-content-tertiary">
                      {status === "running"
                        ? "Processing your request..."
                        : status === "paused"
                          ? "Waiting for your review..."
                          : status === "error"
                            ? "Flow encountered an error"
                            : "Flow completed successfully"}
                    </p>
                  </div>
                  {currentRunId && status !== "running" && (
                    <ViewRunDetailsButton flowId={flowId!} runId={currentRunId} />
                  )}
                </div>

                <div className="space-y-3">
                  {flow.steps.map((step, index) => {
                    const result = stepResults.find((r) => r.stepNumber === step.stepOrder);
                    const stepStatus: StepStatus = result?.status || "idle";

                    return (
                      <div
                        key={step.id}
                        className={(() => {
                          const base = "flex items-center gap-4 p-4 rounded-lg border transition-colors";
                          if (stepStatus === "running") return `${base} bg-blue-500/10 border-blue-500/30`;
                          if (stepStatus === "completed") return `${base} bg-green-500/10 border-green-500/30`;
                          if (stepStatus === "paused") return `${base} bg-yellow-500/10 border-yellow-500/30`;
                          if (stepStatus === "error" || stepStatus === "failed")
                            return `${base} bg-red-500/10 border-red-500/30`;
                          return `${base} bg-surface-secondary/50 border-border-secondary`;
                        })()}
                      >
                        <div className="flex items-center justify-center h-8 w-8 rounded-full bg-surface-secondary text-sm font-medium text-content-tertiary">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-content-primary">
                            {(step.config?.label as string) || step.agentName || "Step"}
                          </p>
                          {result?.tokensUsed ? (
                            <p className="text-xs text-content-tertiary">
                              {result.tokensUsed.toLocaleString()} tokens used
                            </p>
                          ) : null}
                          {result?.outputContent && (
                            <div className="text-xs text-content-secondary mt-1 line-clamp-2">
                              <Markdown content={result.outputContent} />
                            </div>
                          )}
                          {result?.errorMessage && <p className="text-xs text-red-300 mt-1">{result.errorMessage}</p>}
                        </div>
                        {getStatusIcon(stepStatus)}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          {/* Flow Error Banner */}
          {status === "error" && error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-red-400 shrink-0" />
                <div>
                  <h3 className="font-medium text-red-400">Flow Failed</h3>
                  <p className="text-sm text-red-300 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Output Section */}
          {status === "completed" && output && (
            <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5 text-green-400" />
                </div>
                <div className="flex-1">
                  <h2 className="font-medium text-content-primary">Flow Output</h2>
                  <p className="text-sm text-content-tertiary">The final result from all processing steps</p>
                </div>
                {currentRunId && <ViewRunDetailsButton flowId={flowId!} runId={currentRunId} variant="primary" />}
              </div>

              <div className="p-4 bg-surface-secondary rounded-lg prose-sm">
                <Markdown content={output} />
              </div>
            </div>
          )}

          {/* Paused State Message */}
          {status === "paused" && !showReviewModal && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-yellow-400" />
                  <div>
                    <h3 className="font-medium text-content-primary">Flow Paused</h3>
                    <p className="text-sm text-content-tertiary mt-1">
                      This flow is waiting for your review before continuing.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-4">
                  <button
                    onClick={handleRejectReview}
                    disabled={isResuming}
                    className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg font-medium hover:bg-red-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isResuming ? <Loader2 className="h-4 w-4 animate-spin" /> : <AlertCircle className="h-4 w-4" />}
                    Cancel Run
                  </button>
                  {pausedFlowRun && (
                    <button
                      onClick={() => setShowReviewModal(true)}
                      className="px-4 py-2 bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 rounded-lg font-medium hover:bg-yellow-500/30 transition-colors"
                    >
                      Review & Continue
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Review Modal */}
      {showReviewModal &&
        pausedFlowRun &&
        (() => {
          const reviewStep =
            pausedFlowRun.steps?.find((s) => s.status === "paused") ||
            [...(pausedFlowRun.steps || [])].reverse().find((s) => s.status === "completed");
          return reviewStep ? (
            <ReviewModal
              flowRun={pausedFlowRun}
              pausedStep={reviewStep}
              onApprove={handleApproveReview}
              onReject={handleRejectReview}
              onClose={() => setShowReviewModal(false)}
              isSubmitting={isResuming}
            />
          ) : null;
        })()}
    </div>
  );
}
